
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// @components
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'

// @module-components
import AddCourierModal from '@/modules/TMSModule/components/AddCourierModal/index.vue'
import AddCarModal from '@/modules/TMSModule/components/AddCarModal/index.vue'

// @types
import { CityT } from '@/core/types/cities.types'
import { Courier } from '@/new-services/types/couriers'
import { CompanyT } from '@/core/types/companies.types'
import { CouirerCarT, CreateCouirerCar, DispatcherCourierT } from '@/core/types/couriers.types'

// @services
import locationAPI from '@/new-services/location.service'
import tmsAPI from '@/new-services/tms.service'
import couriersAPI from '@/new-services/couriers.service'
import { couriers } from '@/core/realisations/couriers.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// @validations
import useVuelidate from '@vuelidate/core'
import { integer, required, requiredIf } from '@vuelidate/validators'
import { CarrierT } from '@/core/types/carriers.types'
import { carriers } from '@/core/realisations/carriers.realisation'

//constants
import { PHONE_NUMBER_MASK, PRICE_MASK } from '@/core/constants/masks.constants'
import { useMaska } from '@/common/composable/useMaska'

// icons
import iconRefresh from '@/assets/icons/refresh.svg'

// @composable
import { useRoute, useRouter } from 'vue-router'
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'
import { FTLCarTypeT } from '@/core/types/ftl.types'
import { FTL } from '@/core/realisations/FTL.realisation'
import { CheckCostParams } from '@/new-services/types/tms'
import { warehouses } from '@/core/realisations/warehouses.realisation'

export default defineComponent({
    components: {
        NewSelect,
        NewInput,
        PageTitle,
        NewButton,
        FileReader,
        NewCheckbox,
        AddCourierModal,
        AddCarModal,
        NewModal,
        TheTextarea,
        //
        iconRefresh,
    },
    setup() {
        const { unmaskNumbers } = useMaska()
        const notifications = useNotifications()
        const { getFormData } = useFormData()
        const router = useRouter()
        const route = useRoute()

        const fromCityId = Number(route.query.from)
        const toCityId = Number(route.query.to)

        const transitCityIds = route.query.tr ? String(route.query.tr).split(',').map(Number) : []

        const totalWeight = Number(route.query.weight)
        const totalVolume = Number(route.query.volume)

        const payload = reactive({
            carrierId: null as number | null,
            cityIds: [null, null] as (number | null)[],
            // Грузоотправитель
            senderCompanyId: 91,
            loadAddress: null as string | null,
            dateOfLoad: null as string | null,
            sender_full_name: null as string | null,
            sender_phone: null as string | null,
            // Грузополучатель
            receiverCompanyId: 91,
            unloadAddress: null as string | null,
            dateOfUnload: null as string | null,
            receiver_full_name: null as string | null,
            receiver_phone: null as string | null,
            // Хар-ки груза
            natureOfCargo: null as string | null,
            insurance: null as string | null,
            bodyType: null as string | null,
            temperatureRegime: null as string | null,
            costOfTransportation: null as string | null,
            // Данные курьера
            id: null as number | null,
            iin: null as string | null,
            full_name: null as string | null,
            phone_number: null as string | null,
            car_id: null as number | null,
            number: null as string | null,
            brand: null as string | null,
            type: null as string | null,
            vehicleOwnerFullName: null as string | null,
            cubature: null as string | null,
            carCapacityId: null as number | null,
            technicalPassport: null as string | null,
            trailerLicenseNumber: null as string | null,
            identifyCard: null as string | null,
            licence: null as string | null,
            identifyCardDate: null as string | null,
            licenceDate: null as string | null,
            carTechnicalPassportFiles: [] as File[],
            isNds: 0 as number,
            isProject: 0 as number,
            isAdditionalLoad: 0 as number,
            //
            isTripartite: false,
            tripartiteNumber: null as string | null,
            tripartiteDate: null as string | null,
            //
            comment: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                comment: { required },
            },
            payload
        )

        const state = reactive({
            is_loading: false,
            is_cost: false,
            //
            cost: null as number | null,
            direction: null as string | null,
        })

        const courier_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const car_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            carriers: {
                is_loading: false,
                is_failed: false,
                list: [] as CarrierT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            companies: {
                is_loading: false,
                is_failed: false,
                list: [] as CompanyT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            couriers: {
                is_loading: false,
                is_failed: false,
                list: [] as Courier[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            cars: {
                is_loading: false,
                is_failed: false,
                list: [] as CouirerCarT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            ftl_car_types: {
                is_loading: false,
                is_failed: false,
                list: [] as FTLCarTypeT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
        })

        const termAndFormOfPayment = 'Согласно договору'

        const BidTMSValidation = {
            carrierId: { required },
            cityIds: {
                $each: {
                    required,
                },
            },
            //
            senderCompanyId: { required },
            loadAddress: { required },
            dateOfLoad: { required },
            sender_full_name: { required },
            sender_phone: { required },
            //
            receiverCompanyId: { required },
            unloadAddress: { required },
            dateOfUnload: { required },
            receiver_full_name: { required },
            receiver_phone: { required },
            //
            natureOfCargo: { required },
            insurance: { required },
            bodyType: { required },
            temperatureRegime: { required },
            costOfTransportation: { required },
            //
            id: { required },
            iin: { required },
            full_name: { required },
            phone_number: { required },
            car_id: { required },
            number: { required },
            vehicleOwnerFullName: { required },
            cubature: { required, integer },
            carCapacityId: { required },
            identifyCard: { required },
            identifyCardDate: { required },
            carTechnicalPassportFiles: { required },
            licence: { required },
            licenceDate: { required },
            //
            tripartiteNumber: { required: requiredIf(() => payload.isTripartite) },
            tripartiteDate: { required: requiredIf(() => payload.isTripartite) },
        }

        const validation$ = useVuelidate(BidTMSValidation, payload)

        // cities

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        // Перевозчики

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, search, statusId: 2, isExtended: 0 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
                console.error(error)
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2, isExtended: 0 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
                console.error(error)
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        // Компании

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                })
                data_state.companies.list = response.data
                data_state.companies.pagination.total_pages = response.meta.last_page
                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0, statusId: 1 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const loadCouriers = async (page: number, name?: string) => {
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const params = {
                    page,
                    limit: 9999,
                    statusIds: [2],
                    typesIds: [2],
                    name,
                }

                const response = await couriersAPI.getCouriers(params)
                data_state.couriers.list = response.data.data

                data_state.couriers.pagination.total_pages = response.data.meta.last_page

                data_state.couriers.is_failed = false
            } catch (error) {
                data_state.couriers.is_failed = true
                console.error(error)
            } finally {
                data_state.couriers.is_loading = false
            }
        }

        const searchCouriers = (name: string) => {
            loadCouriers(1, name)
        }

        const unshiftFindedCourier = async (courier: Courier) => {
            await loadCouriers(data_state.couriers.pagination.current_page)
            data_state.couriers.list.unshift(courier)
        }

        // Автомобили

        const loadCars = async (page: number, search: string | null = null) => {
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    number: search,
                })

                data_state.cars.list = response.data
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
            } catch (error) {
                data_state.cars.is_failed = true
            } finally {
                data_state.cars.is_loading = false
            }
        }

        const loadMoreCars = async (page: number) => {
            if (page > data_state.cars.pagination.total_pages) return
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                })
                data_state.cars.list = data_state.cars.list.concat(response.data)
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.is_failed = false
            } catch (error) {
                data_state.cars.is_failed = true
            } finally {
                data_state.cars.is_loading = false
            }
        }

        const searchCars = (search_query: string) => {
            loadCars(1, search_query)
        }

        const unshiftFindedCar = async (car: CouirerCarT) => {
            await loadCars(data_state.cars.pagination.current_page)
            data_state.cars.list.unshift(car)
        }

        //

        const getTripartiteDate = () => {
            if (!payload.tripartiteDate) return null

            return payload.tripartiteDate.split('-').reverse().join('.')
        }

        const getPayload = () => {
            const driver = {
                courierId: payload.id,
                iin: payload.iin,
                fullName: payload.full_name,
                phone: unmaskNumbers(payload.phone_number as string),
                vehicleOwnerFullName: payload.vehicleOwnerFullName,
                identifyCard: payload.identifyCard,
                identifyCardDate: payload.identifyCardDate,
                licence: payload.licence,
                licenceDate: payload.licenceDate,
            }

            const car = {
                carId: payload.car_id,
                number: payload.number,
                technicalPassport: payload.technicalPassport,
                brand: payload.brand,
                trailerLicenseNumber: payload.trailerLicenseNumber,
                type: payload.type,
                cubature: payload.cubature,
                carCapacityId: payload.carCapacityId,
            }

            const params: any = {
                carrierId: payload.carrierId,
                cityIds: payload.cityIds,
                //
                senderCompanyId: payload.senderCompanyId,
                loadAddress: payload.loadAddress,
                dateOfLoad: payload.dateOfLoad,
                senderFullName: payload.sender_full_name,
                senderPhone: unmaskNumbers(payload.sender_phone as string),
                isNds: payload.isNds,
                isProject: payload.isProject,
                isAdditionalLoad: payload.isAdditionalLoad,
                //
                receiverCompanyId: payload.receiverCompanyId,
                unloadAddress: payload.unloadAddress,
                dateOfUnload: payload.dateOfUnload,
                receiverFullName: payload.receiver_full_name,
                receiverPhone: unmaskNumbers(payload.receiver_phone as string),
                //
                natureOfCargo: payload.natureOfCargo,
                insurance: payload.insurance,
                bodyType: payload.bodyType,
                temperatureRegime: payload.temperatureRegime,
                costOfTransportation: unmaskNumbers(payload.costOfTransportation as string),
                termAndFormOfPayment,
                //
                driver,
                car,
                carTechnicalPassportFiles: payload.carTechnicalPassportFiles,
                //
                comment: payload.comment,
            }

            if (payload.isTripartite) {
                params.tripartiteNumber = payload.tripartiteNumber
                params.tripartiteDate = getTripartiteDate()
            }

            return params
        }

        const addCity = (index: number) => payload.cityIds.splice(index, 0, null)
        const deleteCity = (index: number) => payload.cityIds.splice(index, 1)

        const getCityPlaceholder = (idx: number) => {
            if (idx === 0) {
                return 'Город отправки'
            } else if (idx === citiesLength.value - 1) {
                return 'Город прибытия'
            } else {
                return 'Город транзит'
            }
        }

        const isAddCity = (idx: number) => idx === citiesLength.value - 1

        const toggleCourier = () => {
            courier_state.is_opened = !courier_state.is_opened
        }

        const toggleCar = () => {
            car_state.is_opened = !car_state.is_opened
        }

        // const fullFillDriverInfo = (data: DoDriverSearchData) => {
        //     payload.full_name = data.fullName
        //     payload.phone_number = data.phone
        //     payload.brand = data.carBrand
        //     payload.type = data.carType
        //     payload.trailerLicenseNumber = data.trailerLicenseNumber
        //     payload.identifyCard = data.identifyCard
        //     payload.identifyCardDate = data.identifyCardDate
        //     payload.licence = data.license
        //     payload.licenceDate = data.licenseDate
        //     payload.number = data.carNumber
        // }

        const createBid = async () => {
            validation$.value.$touch()
            if (validation$.value.$invalid) {
                return
            }

            try {
                state.is_loading = true

                const payloadCreate = getPayload()

                if (!payload) return
                await tmsAPI.createBidTMS(getFormData(payloadCreate))
                router.push('/tms/trips')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onCourierSelect = (id: number) => {
            const driver_selected = data_state.couriers.list.find((driver) => driver.id === id)

            if (driver_selected) {
                payload.iin = driver_selected.iin
                payload.full_name = driver_selected.fullName
                payload.number = driver_selected.car ? driver_selected.car.number : null
                payload.phone_number = driver_selected.phoneNumber

                if (driver_selected.license) {
                    payload.identifyCard = driver_selected.license.identifyCardNumber
                    payload.identifyCardDate = driver_selected.license.identifyCardIssueDate

                    payload.licence = driver_selected.license.driverLicenseNumber
                    payload.licenceDate = driver_selected.license.driverLicenseIssueDate
                }
            }
        }

        const onCarSelect = (id: number) => {
            const car_selected = data_state.cars.list.find((car) => car.id === id)

            if (car_selected) {
                payload.brand = car_selected.model
                payload.type = car_selected.vehicle_type_name
                payload.number = car_selected.number

                payload.carCapacityId = car_selected.ftl_car_type_id
                if (car_selected.cubature) {
                    payload.cubature = car_selected.cubature.toString()
                }

                payload.technicalPassport = car_selected.tech_passport_number
            }
        }

        const setCourier = (response: DispatcherCourierT) => {
            payload.id = response.id
            onCourierSelect(response.id)

            if (response.license) {
                payload.identifyCard = response.license.identifyCardNumber
                payload.identifyCardDate = response.license.identifyCardIssueDate
                payload.licence = response.license.driverLicenseNumber
                payload.licenceDate = response.license.driverLicenseIssueDate
            }
        }

        const createCourier = async (payload: FormData) => {
            try {
                courier_state.is_loading = true

                const response = await couriers.createCourier(payload)

                await loadCouriers(1)
                toggleCourier()
                setCourier(response.data)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании водителя произошла ошибка',
                })
            } finally {
                courier_state.is_loading = false
            }
        }

        const setCar = async (response: CreateCouirerCar, files: File[]) => {
            try {
                await loadCars(1)

                payload.car_id = response.id
                payload.technicalPassport = response.tech_passport_number
                payload.cubature = response.cubature
                payload.carCapacityId = response.ftl_car_type_id
                payload.carTechnicalPassportFiles = files

                onCarSelect(response.id)
            } catch (error) {
                console.error(error)
            }
        }

        const createCar = async (payload: FormData, files: File[]) => {
            try {
                car_state.is_loading = true

                const response = await couriers.createCar(payload)

                toggleCar()
                setCar(response.data, files)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании автомобиля произошла ошибка',
                })
            } finally {
                car_state.is_loading = false
            }
        }

        const loadFtlCarTypes = async () => {
            data_state.ftl_car_types.pagination.current_page = 1

            try {
                data_state.ftl_car_types.is_loading = true
                const response = await FTL.getCarTypes()

                data_state.ftl_car_types.list = response.data
                data_state.ftl_car_types.pagination.total_pages = 1

                data_state.ftl_car_types.is_failed = false
            } catch (error) {
                data_state.ftl_car_types.is_failed = true
            } finally {
                data_state.ftl_car_types.is_loading = false
            }
        }

        const onCreateBid = async () => {
            validation$.value.$touch()
            if (validation$.value.$invalid) {
                return
            }

            try {
                state.is_loading = true

                const cityIdsStr = `[${payload.cityIds.join(',')}]`
                const params: CheckCostParams = {
                    cityIds: cityIdsStr,
                    cost: Number(unmaskNumbers(payload.costOfTransportation as string)),
                    carCapacityId: payload.carCapacityId as number,
                    invoiceType: 1,
                    isNds: payload.isNds,
                }
                const responseCost: any = await tmsAPI.checkCost(params)

                if (!responseCost.data.data.isSuccess && responseCost.data.data.statusId === 15) {
                    state.is_cost = true

                    state.cost = responseCost.data.data.cost
                    state.direction = responseCost.data.data.direction

                    return
                }

                await createBid()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const onSubmitCost = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            state.is_cost = false
            createBid()
        }

        const onCitySelect = async (index: number) => {
            if (index !== 0 && index + 1 !== payload.cityIds.length) {
                return
            }

            if (index === 0) payload.loadAddress = null
            else payload.unloadAddress = null

            try {
                state.is_loading = true

                const params = {
                    cityIds: [Number(payload.cityIds[index])],
                    isTerminal: 1,
                }
                const response = await locationAPI.getWarehouses(params)
                if (response.data.data && response.data.data.length) {
                    const warehouse = response.data.data[0]

                    if (index === 0) payload.loadAddress = warehouse.fullAddressRu
                    else payload.unloadAddress = warehouse.fullAddressRu
                }
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const onCarrierChange = (carrierId: number | null) => {
            if (carrierId) {
                const carrier = data_state.carriers.list.find((c) => c.id === payload.carrierId)

                if (carrier) {
                    payload.isNds = Number(carrier.is_nds)
                }
            } else {
                payload.isNds = 0
            }
        }

        // computed

        const citiesLength = computed(() => payload.cityIds.length)
        const isDelete = (index: number) =>
            index > 0 && index < payload.cityIds.length - 1 && payload.cityIds.length > 2

        //

        const setData = () => {
            let cityIds: number[] = []
            if (transitCityIds.length) {
                cityIds = [fromCityId, ...transitCityIds, toCityId]
            } else {
                cityIds = [fromCityId, toCityId]
            }

            payload.cityIds = [...cityIds]

            if (totalWeight && totalVolume) {
                payload.natureOfCargo = `Вес: ${totalWeight}, объем: ${totalVolume}`
            }
        }

        onBeforeMount(() => {
            loadCities()
            loadCars(1)
            loadCarriers(1)
            loadCompanies(1)
            loadCouriers(1)
            loadFtlCarTypes()

            setData()
        })

        //

        return {
            payload,
            state,
            data_state,
            courier_state,
            car_state,
            state_form$,
            //
            validation$,
            //
            loadCities,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            loadCars,
            loadMoreCars,
            searchCars,
            unshiftFindedCar,
            //
            loadCouriers,
            searchCouriers,
            unshiftFindedCourier,
            //
            addCity,
            deleteCity,
            getCityPlaceholder,
            isAddCity,
            createCourier,
            toggleCourier,
            createCar,
            toggleCar,
            createBid,
            onSubmitCost,
            onCreateBid,
            // resetBid,
            onCourierSelect,
            onCarSelect,
            setCar,
            loadFtlCarTypes,
            //
            citiesLength,
            isDelete,
            //
            PHONE_NUMBER_MASK,
            PRICE_MASK,
            termAndFormOfPayment,
            onCitySelect,
            onCarrierChange,
        }
    },
})
